import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Loader2, X, Save, Upload, ExternalLink, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { SCHEMAS } from '../lib/schemas';
import { adminList, adminCreate, adminUpdate, adminDelete, fetchAuthors } from '../lib/api';
import api from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');

const allFields = (schema) => schema.tabs.flatMap((t) => t.fields);

const emptyFromSchema = (schema) => {
  const o = {};
  allFields(schema).forEach((f) => {
    if (f.type === 'csv' || f.type === 'lines') o[f.key] = [];
    else if (f.type === 'number') o[f.key] = 0;
    else if (f.type === 'boolean') o[f.key] = false;
    else o[f.key] = '';
  });
  return o;
};

const valueToInput = (f, v) => {
  if (v === undefined || v === null) return f.type === 'csv' || f.type === 'lines' ? '' : (f.type === 'number' ? 0 : '');
  if (f.type === 'csv') return Array.isArray(v) ? v.join(', ') : v;
  if (f.type === 'lines') return Array.isArray(v) ? v.join('\n') : v;
  return v;
};

const inputToValue = (f, s) => {
  if (f.type === 'csv') return String(s).split(',').map((x) => x.trim()).filter(Boolean);
  if (f.type === 'lines') return String(s).split('\n').map((x) => x.trim()).filter(Boolean);
  if (f.type === 'number') return Number(s) || 0;
  if (f.type === 'boolean') return !!s;
  return s;
};

// Individual field renderer
const FieldRenderer = ({ f, value, onChange, authors, onImageUpload }) => {
  if (f.type === 'select') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white">
        <option value="">Select...</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === 'author') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white">
        <option value=""> -  No author  - </option>
        {authors.map((a) => <option key={a.id} value={a.id}>{a.name} {a.designation ? `(${a.designation})` : ''}</option>)}
      </select>
    );
  }
  if (f.type === 'boolean') {
    return (
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-teal-600" />
        <span>Yes</span>
      </label>
    );
  }
  if (f.type === 'textarea' || f.type === 'lines') {
    return (
      <textarea rows={4} value={valueToInput(f, value)} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
    );
  }
  if (f.type === 'rich') {
    return <RichTextEditor value={value || ''} onChange={onChange} />;
  }
  if (f.type === 'image') {
    return (
      <div>
        <input type="text" placeholder="Image URL or upload below" value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
        <div className="mt-2 flex items-center gap-2">
          <label className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 text-xs text-slate-700 cursor-pointer hover:bg-slate-50">
            <Upload className="w-3.5 h-3.5" /> Upload
            <input type="file" accept="image/*" hidden onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) { const url = await onImageUpload(file); if (url) onChange(url); }
              e.target.value = '';
            }} />
          </label>
          {value && <img src={value} alt="preview" className="w-12 h-12 rounded object-cover border border-slate-200" />}
        </div>
      </div>
    );
  }
  return (
    <input type={f.type === 'number' ? 'number' : 'text'} step={f.step}
      value={valueToInput(f, value)} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
  );
};

const AdminEntity = () => {
  const { entity } = useParams();
  const schema = SCHEMAS[entity];
  const { toast } = useToast();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const slugTouched = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminList(entity);
      setItems(d);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (schema) {
      load();
      fetchAuthors().then(setAuthors).catch(() => {});
      setActiveTab('basic');
    }
    // eslint-disable-next-line
  }, [entity]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const qq = q.toLowerCase();
    return items.filter((it) => Object.values(it).some((v) => String(v).toLowerCase().includes(qq)));
  }, [q, items]);

  if (!schema) return <div className="text-slate-600">Unknown entity.</div>;

  const openNew = () => { slugTouched.current = false; setActiveTab('basic'); setEditing({ __new: true, ...emptyFromSchema(schema) }); };
  const openEdit = (it) => { slugTouched.current = true; setActiveTab('basic'); setEditing({ ...it }); };

  const updateField = (key, val) => {
    const nameKeys = { colleges: 'short', universities: 'name', courses: 'name', exams: 'name', blogs: 'title', authors: 'name' };
    setEditing((prev) => {
      const next = { ...prev, [key]: val };
      if (key === 'slug') slugTouched.current = true;
      // Auto-fill slug from name field when creating and slug untouched
      if (!slugTouched.current && key === nameKeys[entity]) {
        next.slug = slugify(val);
      }
      return next;
    });
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.url;
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return null;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.__new;
      allFields(schema).forEach((f) => {
        if (payload[f.key] !== undefined && typeof payload[f.key] === 'string') {
          payload[f.key] = inputToValue(f, payload[f.key]);
        }
      });
      if (editing.__new) {
        await adminCreate(entity, payload);
        toast({ title: 'Created' });
      } else {
        await adminUpdate(entity, editing.id, payload);
        toast({ title: 'Updated' });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await adminDelete(entity, toDelete.id);
      toast({ title: 'Deleted' });
      setToDelete(null);
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const publicUrlFor = (it) => {
    const s = it.slug || it.id;
    switch (entity) {
      case 'colleges': return `/colleges/${s}`;
      case 'universities': return `/universities/${s}`;
      case 'courses': return `/courses/${s}`;
      case 'exams': return `/exams/${s}`;
      case 'blogs': return `/blog/${s}`;
      case 'authors': return `/author/${s}`;
      default: return null;
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{schema.title}</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} item{items.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white" />
          </div>
          <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add {schema.title.slice(0, -1)}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-teal-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No items yet. Click "Add" to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {schema.listFields.map((k) => (
                    <th key={k} className="text-left px-4 py-3 font-semibold text-slate-700 capitalize">{k}</th>
                  ))}
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Slug</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    {schema.listFields.map((k) => (
                      <td key={k} className="px-4 py-3 text-slate-700 max-w-xs truncate">{String(it[k] ?? '')}</td>
                    ))}
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{it.slug}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {publicUrlFor(it) && (
                          <a href={publicUrlFor(it)} target="_blank" rel="noreferrer" title="View public page"
                            className="w-8 h-8 grid place-items-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        {['colleges', 'universities', 'courses', 'exams'].includes(entity) && (
                          <button onClick={() => navigate(`/admin/${entity}/${it.id}/subpages`)}
                            title="Manage sub-pages / tabs"
                            className="w-8 h-8 grid place-items-center rounded border border-slate-200 text-teal-700 hover:bg-teal-50">
                            <Layers className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEdit(it)}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setToDelete(it)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editing?.__new ? `Add ${schema.title.slice(0, -1)}` : `Edit ${schema.title.slice(0, -1)}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="bg-slate-100 self-start">
                {schema.tabs.map((t) => (
                  <TabsTrigger key={t.key} value={t.key} className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">{t.label}</TabsTrigger>
                ))}
              </TabsList>
              {schema.tabs.map((tab) => (
                <TabsContent key={tab.key} value={tab.key} className="mt-3 flex-1 overflow-y-auto pr-1">
                  <div className={`grid grid-cols-1 ${tab.key === 'basic' ? 'md:grid-cols-2' : ''} gap-4`}>
                    {tab.fields.map((f) => (
                      <div key={f.key} className={f.type === 'textarea' || f.type === 'lines' || f.type === 'rich' ? 'md:col-span-2' : ''}>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          {f.label}{f.required && <span className="text-rose-500"> *</span>}
                          {f.type === 'slug' && editing.slug && (
                            <span className="ml-2 text-[10px] text-slate-400 font-mono">/{entity}/{editing.slug}</span>
                          )}
                        </label>
                        <FieldRenderer
                          f={f.type === 'slug' ? { ...f, type: 'text' } : f}
                          value={editing[f.key]}
                          onChange={(v) => updateField(f.key, v)}
                          authors={authors}
                          onImageUpload={uploadImage}
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
          <DialogFooter className="mt-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminEntity;
